import { useGetMany } from "ra-core";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";

import type { Company, Contact } from "../types";
import { useAgentActivity, type AgentActivityRun } from "./useAgentActivity";

function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const STATUS_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  completed: "default",
  success: "default",
  running: "secondary",
  pending: "secondary",
  failed: "destructive",
  failure: "destructive",
  cancelled: "outline",
  partial: "outline",
};

const ActivityTargetLink = ({
  run,
  contacts,
  companies,
}: {
  run: AgentActivityRun;
  contacts?: Contact[];
  companies?: Company[];
}) => {
  if (run.target_contact_id) {
    const contact = contacts?.find((c) => Number(c.id) === run.target_contact_id);
    return (
      <Link
        to={`/contacts/${run.target_contact_id}/show`}
        className="hover:underline"
      >
        {contact ? `${contact.first_name} ${contact.last_name}` : `Contact #${run.target_contact_id}`}
      </Link>
    );
  }
  if (run.target_company_id) {
    const company = companies?.find((c) => Number(c.id) === run.target_company_id);
    return (
      <Link
        to={`/companies/${run.target_company_id}/show`}
        className="hover:underline"
      >
        {company?.name ?? `Company #${run.target_company_id}`}
      </Link>
    );
  }
  return null;
};

/**
 * PRD Screen 5, Phase 4, desktop-first as specced. Recent agent_runs rows
 * scoped to actions with a specific contact/company target (excludes
 * untargeted batch/sweep runs) — "what an agent did, when, to which
 * contacts," with a link into the affected record.
 */
export const AgentActivityList = () => {
  const { data: runs, isPending, isError } = useAgentActivity();

  const contactIds = [
    ...new Set((runs ?? []).map((r) => r.target_contact_id).filter((id): id is number => !!id)),
  ];
  const companyIds = [
    ...new Set((runs ?? []).map((r) => r.target_company_id).filter((id): id is number => !!id)),
  ];

  const { data: contacts } = useGetMany<Contact>(
    "contacts",
    { ids: contactIds },
    { enabled: contactIds.length > 0 },
  );
  const { data: companies } = useGetMany<Company>(
    "companies",
    { ids: companyIds },
    { enabled: companyIds.length > 0 },
  );

  return (
    <div className="mt-2 mb-2 flex flex-col gap-4">
      <h1 className="text-xl font-semibold">Agent Activity</h1>
      {isPending && <p className="text-muted-foreground text-sm">Loading…</p>}
      {isError && (
        <p className="text-muted-foreground text-sm italic">
          Couldn't load agent activity.
        </p>
      )}
      {!isPending && !isError && runs?.length === 0 && (
        <p className="text-muted-foreground text-sm italic">
          No targeted agent activity yet.
        </p>
      )}
      {runs && runs.length > 0 && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left">
                <th className="px-3 py-2 font-medium">Agent</th>
                <th className="px-3 py-2 font-medium">Action</th>
                <th className="px-3 py-2 font-medium">Target</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">When</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.id} className="border-b last:border-b-0">
                  <td className="px-3 py-2">
                    <Badge variant="outline">{run.agent_name}</Badge>
                  </td>
                  <td className="px-3 py-2">
                    {run.run_type}
                    {run.trigger_type && (
                      <span className="text-muted-foreground ml-1 text-xs">
                        via {run.trigger_type}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <ActivityTargetLink
                      run={run}
                      contacts={contacts}
                      companies={companies}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={STATUS_VARIANT[run.status] ?? "outline"}>
                      {run.status}
                    </Badge>
                  </td>
                  <td className="text-muted-foreground px-3 py-2 text-xs">
                    {fmtDateTime(run.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
